import type { ScoutToolPart } from "./message/scoutChatMessage";

/** Tool that stages ask-mode mutations as data_change_proposal results. */
export const EXECUTE_GRAPHQL_TOOL_NAME = "execute_graphql";

/** Tool-result kind for staged GraphQL mutations awaiting Accept/Reject. */
export const DATA_CHANGE_PROPOSAL_KIND = "data_change_proposal";

export type ScoutDataChangeProposalField = {
  label: string;
  before?: unknown;
  after?: unknown;
};

export type ScoutDataChangeProposalDiff = {
  verb?: string;
  entityType?: string;
  entityLabel?: string;
  fields: ScoutDataChangeProposalField[];
};

export type ScoutDataChangeProposalImpactRelated = {
  entityType: string;
  count: number;
  samples?: Array<{ id: string; label: string }>;
};

export type ScoutDataChangeProposalImpact = {
  headline: string;
  related: ScoutDataChangeProposalImpactRelated[];
};

export type ScoutDataChangeProposalOperation = {
  mutationRootField: string;
  graphql: string;
  variables?: Record<string, unknown>;
  operationName?: string;
};

export type ScoutDataChangeProposalItem = {
  id: string;
  label: string;
};

/**
 * Oak view model for a staged ask-mode mutation. Mirrors the shared
 * `DataChangeProposal` shape from encamp-scout/core without importing it.
 *
 * For bulk / multi-id mutations, optional `items` lists affected entities and
 * `diff` describes the shared field patch.
 */
export type ScoutDataChangeProposal = {
  proposalId: string;
  summary: string;
  operation: ScoutDataChangeProposalOperation;
  diff: ScoutDataChangeProposalDiff;
  impact?: ScoutDataChangeProposalImpact;
  items?: ScoutDataChangeProposalItem[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: Record<string, unknown>, key: string): string | undefined {
  const candidate = value[key];
  return typeof candidate === "string" ? candidate : undefined;
}

function readOptionalString(value: Record<string, unknown>, key: string): string | undefined {
  const candidate = value[key];
  if (candidate === undefined) return undefined;
  return typeof candidate === "string" ? candidate : undefined;
}

function readField(value: unknown): ScoutDataChangeProposalField | null {
  if (!isRecord(value)) return null;
  const label = readString(value, "label");
  if (!label) return null;
  const field: ScoutDataChangeProposalField = { label };
  if ("before" in value) field.before = value.before;
  if ("after" in value) field.after = value.after;
  return field;
}

function readDiff(value: unknown): ScoutDataChangeProposalDiff | null {
  if (!isRecord(value)) return null;
  if (!Array.isArray(value.fields)) return null;
  const fields: ScoutDataChangeProposalField[] = [];
  for (const entry of value.fields) {
    const field = readField(entry);
    if (field) fields.push(field);
  }
  return {
    verb: readOptionalString(value, "verb"),
    entityType: readOptionalString(value, "entityType"),
    entityLabel: readOptionalString(value, "entityLabel"),
    fields,
  };
}

function readSample(value: unknown): { id: string; label: string } | null {
  if (!isRecord(value)) return null;
  const id = readString(value, "id");
  const label = readString(value, "label");
  if (!id || label === undefined) return null;
  return { id, label };
}

function readRelated(value: unknown): ScoutDataChangeProposalImpactRelated | null {
  if (!isRecord(value)) return null;
  const entityType = readString(value, "entityType");
  if (!entityType) return null;
  if (typeof value.count !== "number" || !Number.isFinite(value.count)) return null;
  const related: ScoutDataChangeProposalImpactRelated = {
    entityType,
    count: value.count,
  };
  if (value.samples !== undefined) {
    if (!Array.isArray(value.samples)) return null;
    const samples: Array<{ id: string; label: string }> = [];
    for (const sample of value.samples) {
      const parsed = readSample(sample);
      if (parsed) samples.push(parsed);
    }
    related.samples = samples;
  }
  return related;
}

function readImpact(value: unknown): ScoutDataChangeProposalImpact | null {
  if (!isRecord(value)) return null;
  const headline = readString(value, "headline");
  if (!headline) return null;
  if (!Array.isArray(value.related)) return null;
  const related: ScoutDataChangeProposalImpactRelated[] = [];
  for (const entry of value.related) {
    const item = readRelated(entry);
    if (item) related.push(item);
  }
  return { headline, related };
}

function readOperation(value: unknown): ScoutDataChangeProposalOperation | null {
  if (!isRecord(value)) return null;
  const mutationRootField = readString(value, "mutationRootField");
  const graphql = readString(value, "graphql");
  if (!mutationRootField || !graphql) return null;
  const operation: ScoutDataChangeProposalOperation = {
    mutationRootField,
    graphql,
  };
  if (value.variables !== undefined) {
    if (!isRecord(value.variables)) return null;
    operation.variables = value.variables;
  }
  const operationName = readOptionalString(value, "operationName");
  if (operationName !== undefined) {
    operation.operationName = operationName;
  }
  return operation;
}

function readProposalItem(value: unknown): ScoutDataChangeProposalItem | null {
  if (!isRecord(value)) return null;
  const id = readString(value, "id");
  const label = readString(value, "label");
  if (!id || label === undefined) return null;
  return { id, label };
}

function readProposalItems(value: unknown): ScoutDataChangeProposalItem[] | null {
  if (!Array.isArray(value)) return null;
  const items: ScoutDataChangeProposalItem[] = [];
  for (const entry of value) {
    const item = readProposalItem(entry);
    if (item) items.push(item);
  }
  return items;
}

/**
 * Parse an `execute_graphql` tool result into a data-change proposal card
 * view model. Returns null when the output is not a staged proposal.
 */
export function parseScoutDataChangeProposal(output: unknown): ScoutDataChangeProposal | null {
  if (!isRecord(output)) return null;
  if (output.kind !== DATA_CHANGE_PROPOSAL_KIND) return null;
  if (output.status !== "awaiting_user_confirmation") return null;
  const proposalId = readString(output, "proposalId");
  const summary = readString(output, "summary");
  if (!proposalId || summary === undefined) return null;
  const operation = readOperation(output.operation);
  const diff = readDiff(output.diff);
  if (!operation || !diff) return null;

  const proposal: ScoutDataChangeProposal = {
    proposalId,
    summary,
    operation,
    diff,
  };
  if (output.impact !== undefined) {
    const impact = readImpact(output.impact);
    if (!impact) return null;
    proposal.impact = impact;
  }
  if (output.items !== undefined) {
    const items = readProposalItems(output.items);
    if (!items) return null;
    if (items.length > 0) {
      proposal.items = items;
    }
  }
  return proposal;
}

export function isScoutDataChangeProposalTool(toolPart: ScoutToolPart): boolean {
  return toolPart.toolName === EXECUTE_GRAPHQL_TOOL_NAME;
}

/**
 * Normalize staging verbs to customer-facing Create / Update / Delete.
 * Older proposals may still carry Upsert/Add/Remove from name inference.
 */
export function normalizeDataChangeDisplayVerb(verb: string | undefined): string | undefined {
  if (!verb || verb.length === 0) return undefined;
  const lower = verb.toLowerCase();
  if (lower === "create" || lower === "add") return "Create";
  if (
    lower === "update" ||
    lower === "upsert" ||
    lower === "set" ||
    lower === "archive" ||
    lower === "restore" ||
    lower === "close" ||
    lower === "open"
  ) {
    return "Update";
  }
  if (lower === "delete" || lower === "remove") return "Delete";
  return verb.charAt(0).toUpperCase() + verb.slice(1).toLowerCase();
}

/** Card title: `Create task · "File Tier II…"` — never camelCase mutation names. */
export function formatDataChangeProposalTitle(proposal: ScoutDataChangeProposal): string {
  const verb = normalizeDataChangeDisplayVerb(proposal.diff.verb);
  const { entityType, entityLabel } = proposal.diff;
  const verbEntity = [verb, entityType].filter((part) => part && part.length > 0).join(" ");
  const itemCount = proposal.items?.length ?? 0;
  // Bulk cards: prefer a count chrome (`Update facilities · 12`) over quoting
  // a synthesized "12 facilities" entityLabel.
  if (itemCount >= 2) {
    if (verbEntity.length > 0) return `${verbEntity} · ${itemCount}`;
    return `${itemCount} entities`;
  }
  if (verbEntity.length > 0 && entityLabel && entityLabel.length > 0) {
    return `${verbEntity} · "${entityLabel}"`;
  }
  if (verbEntity.length > 0) return verbEntity;
  if (entityLabel && entityLabel.length > 0) return entityLabel;
  return "Proposed change";
}

/** Whether the proposal should render the expanded impact block. */
export function isComplexDataChangeProposal(proposal: ScoutDataChangeProposal): boolean {
  return proposal.impact !== undefined && proposal.impact.headline.length > 0;
}

const HIDDEN_DISPLAY_FIELD_KEYS = new Set([
  "id",
  "tenantId",
  "tenantid",
  "createdAt",
  "updatedAt",
  "deletedAt",
  "__typename",
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function humanizeFieldLabel(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (spaced.length === 0) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function humanizeEnumToken(value: string): string {
  if (value.length === 0) return value;
  if (!/[_-]/.test(value) && value !== value.toUpperCase()) return value;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value;
  if (value.includes("@") || value.includes("://")) return value;
  if (!/_/.test(value)) return value;
  const parts = value.split(/_+/).filter((part) => part.length > 0);
  if (parts.length === 0) return value;
  return parts
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      if (/^[ivxlcdm]+$/i.test(part) && part.length <= 4) return part.toUpperCase();
      if (/^[A-Z]{2,5}$/.test(part)) return part;
      const lower = part.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function shouldHideDisplayFieldKey(key: string): boolean {
  if (HIDDEN_DISPLAY_FIELD_KEYS.has(key)) return true;
  if (/Ids?$/i.test(key)) return true;
  return false;
}

function formatLeafDisplayValue(value: unknown): string {
  if (value === null) return "Empty";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (value.length === 0) return "Empty";
    return humanizeEnumToken(value);
  }
  return String(value);
}

function collectDisplayFieldsFromObject(
  source: Record<string, unknown>,
  pathLabels: string[],
  out: ScoutDataChangeProposalField[]
): void {
  for (const [key, value] of Object.entries(source)) {
    if (shouldHideDisplayFieldKey(key)) continue;
    if (value === undefined) continue;
    const nextLabels = [...pathLabels, humanizeFieldLabel(key)];

    if (isRecord(value)) {
      collectDisplayFieldsFromObject(value, nextLabels, out);
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      if (value.every((entry) => isRecord(entry))) continue;
      if (!value.every((entry) => entry === null || typeof entry !== "object")) continue;
      // UUID id lists belong in proposal.items, not the shared field grid.
      if (value.every((entry) => typeof entry === "string" && UUID_RE.test(entry))) continue;
      out.push({
        label: nextLabels.join(" · "),
        after: value.map((entry) => formatLeafDisplayValue(entry)).join(", "),
      });
      continue;
    }

    if (typeof value === "string" && UUID_RE.test(value)) continue;
    if (value !== null && typeof value === "object") continue;

    out.push({
      label: nextLabels.join(" · "),
      after: formatLeafDisplayValue(value),
    });
  }
}

function pickVariablesSource(
  variables: Record<string, unknown> | undefined
): Record<string, unknown> | null {
  if (!variables) return null;
  for (const key of ["input", "data", "payload"] as const) {
    const candidate = variables[key];
    if (isRecord(candidate)) return candidate;
  }
  const entries = Object.entries(variables);
  if (entries.length === 1) {
    const onlyValue = entries[0]?.[1];
    if (isRecord(onlyValue)) return onlyValue;
  }
  return variables;
}

function fieldNeedsFlattening(field: ScoutDataChangeProposalField): boolean {
  const values = [field.after, field.before];
  for (const value of values) {
    if (isRecord(value)) return true;
    if (Array.isArray(value) && value.some((entry) => isRecord(entry))) return true;
    // Staging previously dumped nested objects via JSON.stringify into `after`.
    if (typeof value === "string" && value.trimStart().startsWith("{")) return true;
  }
  return false;
}

/**
 * Customer-facing field rows. Flattens nested objects (and rebuilds from
 * operation.variables when the staged diff is a raw JSON blob) so cards never
 * show unreadable JSON to users.
 */
export function getDisplayDiffFields(
  proposal: ScoutDataChangeProposal
): ScoutDataChangeProposalField[] {
  const staged = proposal.diff.fields;
  const needsFlatten = staged.length === 0 || staged.some(fieldNeedsFlattening);

  if (!needsFlatten) {
    return staged.map((field) => ({
      ...field,
      ...(field.after !== undefined ? { after: formatDataChangeFieldValue(field.after) } : {}),
      ...(field.before !== undefined ? { before: formatDataChangeFieldValue(field.before) } : {}),
    }));
  }

  // Prefer rebuilding from variables — covers `{ data: {…} }` dumps labeled "Data".
  const fromVariables = pickVariablesSource(proposal.operation.variables);
  if (fromVariables) {
    const rebuilt: ScoutDataChangeProposalField[] = [];
    collectDisplayFieldsFromObject(fromVariables, [], rebuilt);
    if (rebuilt.length > 0) return rebuilt;
  }

  // Fall back to flattening whatever object blobs are already on the field rows.
  const flattened: ScoutDataChangeProposalField[] = [];
  for (const field of staged) {
    const value = field.after ?? field.before;
    if (isRecord(value)) {
      collectDisplayFieldsFromObject(value, [field.label], flattened);
      continue;
    }
    if (typeof value === "string" && value.trimStart().startsWith("{")) {
      try {
        const parsed: unknown = JSON.parse(value);
        if (isRecord(parsed)) {
          collectDisplayFieldsFromObject(parsed, [field.label], flattened);
          continue;
        }
      } catch {
        // keep as opaque string below
      }
    }
    if (fieldNeedsFlattening(field)) continue;
    flattened.push({
      label: field.label,
      ...(field.after !== undefined ? { after: formatDataChangeFieldValue(field.after) } : {}),
      ...(field.before !== undefined ? { before: formatDataChangeFieldValue(field.before) } : {}),
    });
  }
  return flattened;
}

/** Display string for a diff field value (before/after). Never dump raw JSON. */
export function formatDataChangeFieldValue(value: unknown): string {
  if (value === undefined) return "—";
  if (value === null) return "Empty";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (value.length === 0) return "Empty";
    // Staging already humanizes most values; still soften SCREAMING_SNAKE leftovers.
    return humanizeEnumToken(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "Empty";
    if (value.every((entry) => typeof entry !== "object" || entry === null)) {
      return value.map((entry) => formatDataChangeFieldValue(entry)).join(", ");
    }
    return `${value.length} items`;
  }
  if (typeof value === "object") {
    // Nested blobs should be flattened at staging time; never show JSON to users.
    return "—";
  }
  return String(value);
}

/** Status label shown on resolved cards. */
export function formatDataChangeResponseStatus(
  status: "confirmed" | "declined"
): "Approved" | "Rejected" {
  return status === "confirmed" ? "Approved" : "Rejected";
}
